<template>
  <div class="order-summary">
    <div class="title-row">
      <button type="button" class="back-button" title="Retour à la liste des dossiers" @click="backCallback()"><span class="fas fa-arrow-left"></span></button>
      <div class="title-block">
        <p class="eyebrow">Dossier N°{{ order.number }} <StatusBadge :status="order.stage" :label="stageLabel(order.stage)" /></p>
        <h2 :title="order.title">{{ order.title || 'Sans titre' }}</h2>
        <p v-if="order.clientNames && order.clientNames.length > 0" class="clients"><span class="fas fa-user"></span>{{ order.clientNames.join(' · ') }}</p>
      </div>
      <div class="actions">
        <button type="button" class="button-icon-custom" title="Modifier le dossier" @click="editCallback()"><span class="fas fa-pen"></span></button>
        <button v-if="order.businesses.length === 0" type="button" class="button-icon-custom" title="Supprimer le dossier" @click="deleteCallback()"><span class="fas fa-trash"></span></button>
      </div>
    </div>

    <div class="kpis" :class="{'only-sales': !finance.hasPurchase}">
      <div class="kpi">
        <span class="kpi-label">Vendu HT</span>
        <span class="kpi-value">{{ formatAmount(finance.sold) }}</span>
        <span class="kpi-hint" v-if="finance.pending > 0">+ {{ formatAmount(finance.pending) }} de devis en attente</span>
        <span class="kpi-hint" v-else>Devis acceptés et factures, avoirs déduits</span>
      </div>
      <template v-if="finance.hasPurchase">
        <div class="kpi">
          <span class="kpi-label">Acheté {{ finance.basis }}</span>
          <span class="kpi-value">{{ formatAmount(finance.bought) }}</span>
          <span class="kpi-hint" v-if="finance.toReview > 0">+ {{ formatAmount(finance.toReview) }} à vérifier</span>
          <span class="kpi-hint" v-else>{{ finance.count }} achat{{ finance.count > 1 ? 's' : '' }} rattaché{{ finance.count > 1 ? 's' : '' }}</span>
        </div>
        <div class="kpi" :class="marginClass">
          <span class="kpi-label">Marge{{ finance.basis === 'HT' ? ' HT' : '' }}</span>
          <span class="kpi-value">{{ formatAmount(finance.margin) }}</span>
          <span class="kpi-hint">{{ finance.marginRate !== undefined ? formatRate(finance.marginRate) + ' du vendu' : 'Aucun montant vendu' }}</span>
        </div>
        <div class="kpi">
          <span class="kpi-label">Reste à payer</span>
          <span class="kpi-value">{{ formatAmount(finance.remaining) }}</span>
          <span class="kpi-hint">TTC, aux fournisseurs</span>
        </div>
      </template>
    </div>

    <div v-if="finance.hasPurchase && finance.sold > 0" class="ratio" :title="'Les achats représentent ' + formatRate(boughtRate) + ' du vendu'">
      <span class="ratio-bought" :class="{over: finance.bought > finance.sold}" :style="{width: Math.min(100, boughtRate) + '%'}"></span>
    </div>
  </div>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faArrowLeft, faPen, faTrash, faUser} from "@fortawesome/free-solid-svg-icons";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {formatAmount, formatRate} from "@/purchase/utils";
import {stageLabel} from "@/components/production/productionUtils";
library.add(faArrowLeft, faPen, faTrash, faUser);

// En-tête de la fiche d'un dossier : titre, client, étape, et les chiffres clés (vendu, acheté, marge, reste à payer).
// editCallback / deleteCallback / backCallback sont fournis par la vue.
export default {
  name: 'OrderSummary',
  components: {StatusBadge},
  props: ['order', 'finance', 'editCallback', 'deleteCallback', 'backCallback'],
  computed: {
    marginClass() {
      if (!this.finance.sold && !this.finance.bought) return '';
      return this.finance.margin < 0 ? 'negative' : 'positive';
    },
    boughtRate() {
      return this.finance.sold > 0 ? Math.round(this.finance.bought / this.finance.sold * 1000) / 10 : 0;
    },
  },
  methods: {
    formatAmount,
    formatRate,
    stageLabel,
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.order-summary
  padding 18px 20px 16px
  background white
  border 1px solid #e3e7ef
  border-radius 14px

.title-row
  display flex
  align-items flex-start
  gap 12px

.back-button
  display none
  flex 0 0 auto
  width 34px
  height 34px
  border none
  border-radius 10px
  background #eef1f7
  color THEME_COLOR_PRIMARY
  cursor pointer

.title-block
  flex 1
  min-width 0

  .eyebrow
    display flex
    align-items center
    gap 10px
    margin 0
    font-size 12px
    font-weight 700
    letter-spacing 0.06em
    text-transform uppercase
    color #8a93a8

  h2
    margin 2px 0 0
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    font-size 24px
    color THEME_COLOR_PRIMARY

  .clients
    display flex
    align-items center
    gap 8px
    margin 4px 0 0
    font-size 14px
    color #5f6b85

    span
      font-size 12px
      color #98a1b6

.actions
  display flex
  gap 4px

.kpis
  display grid
  grid-template-columns repeat(4, 1fr)
  gap 12px
  margin-top 16px

  &.only-sales
    grid-template-columns 1fr

.kpi
  display flex
  flex-direction column
  gap 2px
  padding 12px 14px
  border-radius 12px
  background #f5f7fb

  .kpi-label
    font-size 11.5px
    font-weight 700
    letter-spacing 0.05em
    text-transform uppercase
    color #7a849b

  .kpi-value
    font-size 22px
    font-weight 700
    color FONT_COLOR

  .kpi-hint
    font-size 12px
    color #8a93a8

  &.positive
    background #e7f6ec

    .kpi-value
      color #1f7a45

  &.negative
    background #fcebea

    .kpi-value
      color #c0392b

.ratio
  height 6px
  margin-top 14px
  border-radius 6px
  background #dfeee5
  overflow hidden

  .ratio-bought
    display block
    height 100%
    border-radius 6px
    background #7f93c4

    &.over
      background #e0877d

@media (max-width: 1100px)
  .kpis
    grid-template-columns repeat(2, 1fr)

@media (max-width: 1000px)
  .back-button
    display block

@media (max-width: 576px)
  .kpis
    grid-template-columns 1fr 1fr

  .title-block h2
    font-size 20px
</style>
